/**
 * PlanSection.tsx — the patient portal's "My plan" section (PATIENT-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 10 (patient
 * portal basis, 2026-07-22).
 *
 * WHY: the focused view of the patient's OWN subscription — treatment, term,
 * monthly price, renewal countdown (months remaining + next payment) and the
 * plan state. This is the patient looking at THEIR OWN plan, so the price is
 * shown in full — this is NOT a staff money-redaction surface (money is integer
 * PENCE; 0 → "price set at launch"). A fresh account with no plan gets the warm
 * empty state (a 404 on the dashboard read is that same empty state, never an
 * error — matching Dashboard.tsx). Every fetch has a retryable error (TA-06).
 *
 * CHANGE / CANCEL — LATER SCOPE (Wave 10 hook): the patient will be able to
 * change or cancel-at-term from here, but the mutation is deliberately NOT built
 * yet (CTO: "leave a hook, don't build the mutation"). The `ManagePlanHook`
 * below is where that lands — see the inline HOOK comment for the exact wiring.
 *
 * RE-SUBSCRIBE (end-of-term, 2026-08-15) IS THE ONE MUTATION THAT DID LAND, and
 * only because it is not a change to the existing plan: it starts a NEW term
 * that a clinician must approve, so nothing about the finished plan is edited
 * from here. `ResubscribeCard` renders itself away entirely unless the SERVER
 * says this plan is eligible — the portal never infers that from `state`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { getDashboard } from '../api/orders';
import type { CurrentPlan, DashboardData } from '../api/types';
import { formatMonthly } from '../lib/format';
import { planCardPresentation } from '../lib/dashboardSummary';
import { displayTreatmentName, planStatusCopy } from '../lib/patientStatusCopy';
import { ButtonArrow, buttonClass, buttonStyle, Card, ErrorState, Help, PageFrame, Title } from '../ui/kit';
import { IconPlans } from '../ui/icons';
import { CardIconHeader, Row, SectionTitle } from '../ui/sections';
import { PatientStatusBadge } from '../ui/PatientStatusBadge';
import { SkeletonCard } from '../ui/Skeleton';
import { ResubscribeCard } from './ResubscribeCard';

/** The empty dashboard shape a fresh account is treated as (matches Dashboard.tsx). */
const EMPTY_DASHBOARD: DashboardData = { currentPlan: null, orders: [], bills: [] };

export function PlanSection() {
  const [plan, setPlan] = useState<CurrentPlan | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setState('loading');
    getDashboard()
      .catch((err: unknown) => {
        // A brand-new account may 404 (no dashboard row minted yet) — that is
        // the empty state, not a fetch failure (mirrors Dashboard.tsx).
        if (err instanceof ApiError && err.status === 404) return EMPTY_DASHBOARD;
        throw err;
      })
      .then((dashboard) => {
        setPlan(dashboard.currentPlan);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state === 'loading') {
    return (
      <PageFrame bare>
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
          <SkeletonCard lines={5} label="Loading your plan…" />
        </div>
      </PageFrame>
    );
  }

  if (state === 'error') {
    return (
      <PageFrame bare>
        <ErrorState message="Sorry — we could not load your plan." onRetry={load} />
      </PageFrame>
    );
  }

  return (
    <PageFrame bare>
      <Title as="h2">My plan</Title>
      {plan ? <PlanCard plan={plan} /> : <NoPlanCard />}
    </PageFrame>
  );
}

function PlanCard({ plan }: { plan: CurrentPlan }) {
  const pres = planCardPresentation(plan);
  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <Card className="gps-card-interactive">
        <CardIconHeader icon={<IconPlans />} title="Your subscription" action={<PatientStatusBadge status={planStatusCopy(plan.state)} />} />
        <Row label="Treatment" value={displayTreatmentName(plan.medicationName, plan.productKey)} />
        <Row label="Plan term" value={`${plan.months} month${plan.months === 1 ? '' : 's'}`} />
        {/* Own-plan price shown in full — the patient legitimately sees their own money. */}
        <Row label="Price" value={formatMonthly(plan.monthlyPence)} />
        <Row label="Months remaining" value={pres.monthsRemainingValue} />
        <Row label="Next payment" value={pres.nextPaymentValue} />
        {pres.help ? <Help style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>{pres.help}</Help> : null}
      </Card>
      {/* End-of-term re-subscribe. Sits ABOVE "Manage your plan" because a
          finished plan's only live action is starting a new one — and renders
          nothing at all when the server has not said it is eligible. */}
      <ResubscribeCard plan={plan} />
      <ManagePlanHook />
    </div>
  );
}

/**
 * The change / cancel-at-term entry point — LATER SCOPE (Wave 10). This renders
 * the read-only signpost only; the mutation is intentionally not built yet.
 *
 * HOOK (do NOT wire the mutation in this slice): when the change/cancel lane
 * ships, replace the "contact us" copy with the real controls — an orders
 * client call (e.g. `POST /v1/orders/subscriptions/:id/cancel-at-term` /
 * `…/change`) behind a ConfirmDialog, keyed off `plan.orderCaseId` /
 * subscriptionId. Everything upstream (the plan read, the section chrome) is
 * already in place, so only the action + its client need adding here.
 */
function ManagePlanHook() {
  return (
    <Card>
      <SectionTitle>Manage your plan</SectionTitle>
      <Help style={{ marginBottom: 0 }}>
        Need to change or cancel your plan? Please contact us and a member of the team will help —
        self-serve changes are coming soon.
      </Help>
    </Card>
  );
}

function NoPlanCard() {
  return (
    <Card style={{ textAlign: 'center', padding: 'var(--gps-s-6) var(--gps-s-4)' }}>
      <SectionTitle>No active plan yet</SectionTitle>
      <Help style={{ maxWidth: '24rem', margin: '0 auto var(--gps-s-4)' }}>
        Once a clinician approves your treatment, your plan and next payment will appear here.
      </Help>
      <div style={{ maxWidth: '20rem', margin: '0 auto' }}>
        <Link to="/weightloss" className={buttonClass('cta')} style={buttonStyle('cta')}>
          Start your 2-minute check
          <ButtonArrow />
        </Link>
      </div>
    </Card>
  );
}
